import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import Typography from "@mui/material/Typography";
import { useState } from "react";

interface CdnImageProps {
  src: string;
  alt: string;
  fallbackSrc?: string;
  sx?: SxProps<Theme>;
}

type SxArray = Extract<NonNullable<SxProps<Theme>>, readonly unknown[]>;
type SxArrayItem =
  | boolean
  | SystemStyleObject<Theme>
  | ((theme: Theme) => SystemStyleObject<Theme>);

function isSxArray(value: SxProps<Theme> | undefined): value is SxArray {
  return Array.isArray(value);
}

export function CdnImage({ src, alt, fallbackSrc, sx }: CdnImageProps) {
  const [activeSrc, setActiveSrc] = useState(src);
  const [hasFailed, setHasFailed] = useState(false);
  const normalizedSx: readonly SxArrayItem[] = isSxArray(sx)
    ? sx
    : sx
      ? [sx]
      : [];

  if (hasFailed && !fallbackSrc) {
    return (
      <Box
        role="img"
        aria-label={`${alt} unavailable`}
        sx={[
          {
            width: "100%",
            minHeight: 120,
            borderRadius: 1,
            border: "1px solid rgba(255,255,255,0.18)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(255,255,255,0.03)",
          },
          ...normalizedSx,
        ]}
      >
        <Typography variant="caption" sx={{ opacity: 0.75 }}>
          Image unavailable
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      component="img"
      alt={alt}
      src={activeSrc}
      sx={sx}
      onError={() => {
        if (fallbackSrc && activeSrc !== fallbackSrc) {
          setActiveSrc(fallbackSrc);
          return;
        }

        setHasFailed(true);
      }}
    />
  );
}
